// zod enums. Every database enum derives from the `const` tuple in `src/enums.ts` (fix #4): the
// CHECK constraints and these schemas share one source, and `enums-sql.test.ts` fails on drift.
// Contract-only enums (values that never reach a CHECK) are defined at the bottom.
import { z } from "zod";
import * as e from "../enums";

// cp
export const AgencyGrantLevel = z.enum(e.agencyGrantLevel);
export const AgencyUserRole = z.enum(e.agencyUserRole);
export const BillingInterval = z.enum(e.billingInterval);
export const AddonKey = z.enum(e.addonKey);
export const Currency = z.enum(e.supportedCurrency);
export const AddonDependencyMode = z.enum(e.addonDependencyMode);
export const EntitlementStatus = z.enum(e.entitlementStatus);
export const EntitlementSource = z.enum(e.entitlementSource);

// cell: identity and org
export const CompanyKind = z.enum(e.companyKind);
export const MembershipKind = z.enum(e.membershipKind);
export const SeatHolderKind = z.enum(e.seatHolderKind);
export const MembershipStatus = z.enum(e.membershipStatus);
export const InvitationStatus = z.enum(e.invitationStatus);
export const InvitationRole = z.enum(e.invitationRole);
export const SeatKind = z.enum(e.seatKind);
export const SeatStatus = z.enum(e.seatStatus);
export const SeatOrigin = z.enum(e.seatOrigin);
export const SeatDeFill = z.enum(e.seatDeFill);
export const SeatAssignmentCapacity = z.enum(e.seatAssignmentCapacity);
export const DigitalEmployeeStatus = z.enum(e.digitalEmployeeStatus);
export const WorkforceAgentStatus = z.enum(e.workforceAgentStatus);
export const WorkforceAgentReportsToKind = z.enum(e.workforceAgentReportsToKind);
export const AgentKeyKind = z.enum(e.agentKeyKind);
export const AgentKeyStatus = z.enum(e.agentKeyStatus);
export const AgentKeyScope = z.enum(e.agentKeyScope);
export const IngestKeyScope = z.enum(e.ingestKeyScope);
export const SourceKeyScope = z.enum(e.sourceKeyScope);
export const IngestKeySource = z.enum(e.ingestKeySource);
export const MeterSettlementResolution = z.enum(e.meterSettlementResolution);
export const ExternalIdentityKind = z.enum(e.externalIdentityKind);
export const TeamComposition = z.enum(e.teamComposition);
export const SeatEdgeKind = z.enum(e.seatEdgeKind);
export const ResponsibilityStatus = z.enum(e.responsibilityStatus);
export const RecurringWorkStatus = z.enum(e.recurringWorkStatus);
export const GoalKind = z.enum(e.goalKind);
export const GoalState = z.enum(e.goalState);
export const GoalTrack = z.enum(e.goalTrack);
// M8-ASSESS
export const AssessmentQuestionnaire = z.enum(e.assessmentQuestionnaire);
export const AssessmentRoundState = z.enum(e.assessmentRoundState);
export const AssessmentParticipantKind = z.enum(e.assessmentParticipantKind);
export const AssessmentParticipantState = z.enum(e.assessmentParticipantState);
export const AssessmentProposalKind = z.enum(e.assessmentProposalKind);
export const AssessmentProposalState = z.enum(e.assessmentProposalState);
export const IssueStatus = z.enum(e.issueStatus);
export const IssueResolutionKind = z.enum(e.issueResolutionKind);
export const IssueEscalation = z.enum(e.issueEscalation);
export const IssueSourceKind = z.enum(e.issueSourceKind);
export const MeetingSeriesKind = z.enum(e.meetingSeriesKind);
export const MeetingState = z.enum(e.meetingState);
export const MeetingPrereadState = z.enum(e.meetingPrereadState);
export const AgendaSection = z.enum(e.agendaSection);
export const AgendaSourceKind = z.enum(e.agendaSourceKind);
export const AgendaOutcomeKind = z.enum(e.agendaOutcomeKind);
export const MeetingOutcomeKind = z.enum(e.meetingOutcomeKind);
export const CalendarKind = z.enum(e.calendarKind);
export const CalendarVisibility = z.enum(e.calendarVisibility);
export const CalendarEventStatus = z.enum(e.calendarEventStatus);
export const CalendarEventVisibility = z.enum(e.calendarEventVisibility);
export const CalendarEventSource = z.enum(e.calendarEventSource);
export const CalendarAttendeeResponse = z.enum(e.calendarAttendeeResponse);
export const CalendarShareLevel = z.enum(e.calendarShareLevel);
export const CalendarShareGrantee = z.enum(e.calendarShareGrantee);
export const CalendarRevisionReason = z.enum(e.calendarRevisionReason);
export const WebhookStatus = z.enum(e.webhookStatus);
export const WebhookDeliveryStatus = z.enum(e.webhookDeliveryStatus);
// WEBHOOKS-IN
export const InboundWebhookVerification = z.enum(e.inboundWebhookVerification);
export const InboundWebhookStatus = z.enum(e.inboundWebhookStatus);
export const InboundWebhookScreenMode = z.enum(e.inboundWebhookScreenMode);
export const InboundDeliveryStatus = z.enum(e.inboundDeliveryStatus);
export const InboundDeliveryVerification = z.enum(e.inboundDeliveryVerification);
export const AccountGrantLevel = z.enum(e.accountGrantLevel);
// FOUNDER-OS
export const AccountCompanyRelation = z.enum(e.accountCompanyRelation);
export const FounderBriefCadence = z.enum(e.founderBriefCadence);
export const FounderBriefSource = z.enum(e.founderBriefSource);

// cell: spaces, chat, inbox, boards and tasks (M2-01)
export const SpaceKind = z.enum(e.spaceKind);
export const ChannelKind = z.enum(e.channelKind);
export const ChannelMemberRole = z.enum(e.channelMemberRole);
export const ChannelPostingPolicy = z.enum(e.channelPostingPolicy);
export const ThreadParticipantRole = z.enum(e.threadParticipantRole);
export const ChannelNotifyLevel = z.enum(e.channelNotifyLevel);
export const MessageAuthorKind = z.enum(e.messageAuthorKind);
/** M2-04 (the API contract names its own `AgentWakeRule` in contracts/api/chat.ts). */
const AgentWakeRuleEnum = z.enum(e.agentWakeRule);
export const TopicStatus = z.enum(e.topicStatus);
export const TopicMemberVia = z.enum(e.topicMemberVia);
export const ThreadKind = z.enum(e.threadKind);
export const AttachmentSubjectKind = z.enum(e.attachmentSubjectKind);
export const AttachmentState = z.enum(e.attachmentState);
export const PageUploadStatus = z.enum(e.pageUploadStatus);
export const FollowSubjectKind = z.enum(e.followSubjectKind);
export const FollowLevel = z.enum(e.followLevel);
export const InboxItemKind = z.enum(e.inboxItemKind);
export const InboxSubjectKind = z.enum(e.inboxSubjectKind);
export const DigestFrequency = z.enum(e.digestFrequency);
export const NotificationCategory = z.enum(e.notificationCategory);
export const NotificationPreset = z.enum(e.notificationPreset);
export const ReplyLength = z.enum(e.replyLength);
export const NotificationOverrideLevel = z.enum(e.notificationOverrideLevel);
export const SearchHitKind = z.enum(e.searchHitKind);
export const BoardDigestInterval = z.enum(e.boardDigestInterval);
export const BoardColor = z.enum(e.boardColor);
export const FieldType = z.enum(e.fieldType);
export const FieldAudience = z.enum(e.fieldAudience);
export const ViewKind = z.enum(e.viewKind);
export const TaskOrigin = z.enum(e.taskOrigin);
export const TaskDependencyKind = z.enum(e.taskDependencyKind);
// WOS-BUNDLE
export const TagColor = z.enum(e.tagColor);
export const TaskLinkKind = z.enum(e.taskLinkKind);
export const TaskLinkRelation = z.enum(e.taskLinkRelation);
export const FavoriteKind = z.enum(e.favoriteKind);
export const RecentKind = z.enum(e.recentKind);
export const MyTaskBuiltinSection = z.enum(e.myTaskBuiltinSection);
export const PageKind = z.enum(e.pageKind);
export const KnowledgeSourceKind = z.enum(e.knowledgeSourceKind);

// cell + cp: agents, runs, approvals, finance controls (M3-01)
export const AgentTemplateScope = z.enum(e.agentTemplateScope);
export const AgentTemplateKind = z.enum(e.agentTemplateKind);
export const AgentTemplateStatus = z.enum(e.agentTemplateStatus);
export const SkillPackScope = z.enum(e.skillPackScope);
export const SkillPackStatus = z.enum(e.skillPackStatus);
export const SkillPackFormat = z.enum(e.skillPackFormat);
export const SkillAdditionVia = z.enum(e.skillAdditionVia);
export const AgentProfileOwnerKind = z.enum(e.agentProfileOwnerKind);
export const AgentProfileRole = z.enum(e.agentProfileRole);
export const AgentProfileState = z.enum(e.agentProfileState);
export const WakeupKind = z.enum(e.wakeupKind);
export const BoardEventClass = z.enum(e.boardEventClass);
// B5-DECIDE
export const DecisionBand = z.enum(e.decisionBand);
export const DecisionProvider = z.enum(e.decisionProvider);
export const ClassifierDecisionSource = z.enum(e.classifierDecisionSource);
export const GateMode = z.enum(e.gateMode);
export const GateHoldState = z.enum(e.gateHoldState);
export const WorkerRefusalReason = z.enum(e.workerRefusalReason);
export type WorkerRefusalReason = z.infer<typeof WorkerRefusalReason>;
export const WakeupStatus = z.enum(e.wakeupStatus);
export const WakeupSteer = z.enum(e.wakeupSteer);
export const WakeupHoldReason = z.enum(e.wakeupHoldReason);
export const RunEffortRequest = z.enum(e.runEffortRequest);
export type RunEffortRequest = z.infer<typeof RunEffortRequest>;
export const RunEffortTier = z.enum(e.runEffortTier);
export type RunEffortTier = z.infer<typeof RunEffortTier>;
export const RunEffortReasoning = z.enum(e.runEffortReasoning);
export type RunEffortReasoning = z.infer<typeof RunEffortReasoning>;
export const RunEffortSource = z.enum(e.runEffortSource);
export type RunEffortSource = z.infer<typeof RunEffortSource>;
export const WakeTimerStatus = z.enum(e.wakeTimerStatus);
export const HelperChainState = z.enum(e.helperChainState);
export const HelperChainSubjectKind = z.enum(e.helperChainSubjectKind);
export const SettledSubjectKind = z.enum(e.settledSubjectKind);
export const SettledSubjectHow = z.enum(e.settledSubjectHow);
export const HelperCardState = z.enum(e.helperCardState);
export const HelperCardKind = z.enum(e.helperCardKind);
export const RunHandoffKind = z.enum(e.runHandoffKind);
export const CommitmentState = z.enum(e.commitmentState);
export const CommitmentKind = z.enum(e.commitmentKind);
export const CommitmentActorKind = z.enum(e.commitmentActorKind);
export const CommitmentVerify = z.enum(e.commitmentVerify);
export const BlockerCategory = z.enum(e.blockerCategory);
export const CommitmentCause = z.enum(e.commitmentCause);
export const CommitmentSubjectKind = z.enum(e.commitmentSubjectKind);
export const CommitmentLogActorKind = z.enum(e.commitmentLogActorKind);
export const LoopIncidentKind = z.enum(e.loopIncidentKind);
export const LoopIncidentStatus = z.enum(e.loopIncidentStatus);
export const RunState = z.enum(e.runState);
export const RunRetryState = z.enum(e.runRetryState);
export const RunIdentityMode = z.enum(e.runIdentityMode);
export const SandboxKind = z.enum(e.sandboxKind);
export const RunStepKind = z.enum(e.runStepKind);
export const CanvasOpVerb = z.enum(e.canvasOpVerb);
export const CanvasBlockKind = z.enum(e.canvasBlockKind);
export const LiveView = z.enum(e.liveView);
export const CanvasInteraction = z.enum(e.canvasInteraction);
export const ApprovalKind = z.enum(e.approvalKind);
export const ApprovalState = z.enum(e.approvalState);
export const ApprovalExecutionState = z.enum(e.approvalExecutionState);
export const ApprovalExecutionFailure = z.enum(e.approvalExecutionFailure);
export const ApprovalExecutionCode = z.enum(e.approvalExecutionCode);
export const DelegationState = z.enum(e.delegationState);
export const BudgetSubjectKind = z.enum(e.budgetSubjectKind);
export const BudgetState = z.enum(e.budgetState);
export const BudgetPeriodType = z.enum(e.budgetPeriodType);
export const ReservationState = z.enum(e.reservationState);
export const BudgetIncidentKind = z.enum(e.budgetIncidentKind);
export const PriceListScope = z.enum(e.priceListScope);
export const PriceUnit = z.enum(e.priceUnit);
// finance views (M5-03)
export const FinanceRecordStatus = z.enum(e.financeRecordStatus);
export const RevenueKind = z.enum(e.revenueKind);
export const CompensationCadence = z.enum(e.compensationCadence);
export const RecurringExpenseKind = z.enum(e.recurringExpenseKind);
export const RecurringCadence = z.enum(e.recurringCadence);
export const CategoryKind = z.enum(e.categoryKind);
export const HeadcountHolderKind = z.enum(e.headcountHolderKind);
export const ForecastSubjectKind = z.enum(e.forecastSubjectKind);
export const FinanceRelation = z.enum(e.financeRelation);

// cell: personal crews (M4-01)
export const PersonalCrewState = z.enum(e.personalCrewState);
export const PersonalCrewPauseReason = z.enum(e.personalCrewPauseReason);
export const CrewAgentSource = z.enum(e.crewAgentSource);
export const AutonomyMode = z.enum(e.autonomyMode);
export const CrewActionKind = z.enum(e.crewActionKind);
export const SharingRule = z.enum(e.sharingRule);
export const SharingSubjectKind = z.enum(e.sharingSubjectKind);
export const CrewContextLayer = z.enum(e.crewContextLayer);
export const CrewContextVisibility = z.enum(e.crewContextVisibility);
export const CrewCapPeriod = z.enum(e.crewCapPeriod);
// cell: crew features (M4-03)
export const CrewProposalKind = z.enum(e.crewProposalKind);
export const CrewProposalState = z.enum(e.crewProposalState);
export const CrewProposalSource = z.enum(e.crewProposalSource);
export const CrewMemoryBucket = z.enum(e.crewMemoryBucket);
export const CrewMemorySubjectKind = z.enum(e.crewMemorySubjectKind);

// cell: agent rooms (M4-05)
export const AgentRoomTurnPolicy = z.enum(e.agentRoomTurnPolicy);
export const AgentRoomParticipantKind = z.enum(e.agentRoomParticipantKind);
export const StatusAskSubjectKind = z.enum(e.statusAskSubjectKind);
export const StatusAskState = z.enum(e.statusAskState);
export const StatusAskEscalation = z.enum(e.statusAskEscalation);

// catalog + cp + cell: DE configuration (DC-01)
export const TemplateVersionState = z.enum(e.templateVersionState);
export const TemplateVersionSource = z.enum(e.templateVersionSource);
export const TemplateClass = z.enum(e.templateClass);
/**
 * Before migration 0162 the done-for-you class was spelled `htva`. Rows were rewritten, but
 * append-only events and content-hashed bundles keep it: parse them with this, then normalise.
 */
export const LegacyTemplateClass = z.enum(["htva", ...e.templateClass]);
export function normaliseTemplateClass(
  cls: z.infer<typeof LegacyTemplateClass>,
): z.infer<typeof TemplateClass> {
  return cls === "htva" ? "managed" : cls;
}
export const TemplateEvalKind = z.enum(e.templateEvalKind);
export const TemplateEvalResult = z.enum(e.templateEvalResult);
export const EnforcerKind = z.enum(e.enforcerKind);
export const TemplateEditSource = z.enum(e.templateEditSource);
export const TemplateEditState = z.enum(e.templateEditState);
export const VerificationState = z.enum(e.verificationState);
export const DeployPath = z.enum(e.deployPath);
export const DeployKind = z.enum(e.deployKind);
export const DeployState = z.enum(e.deployState);
export const DeployErrorCode = z.enum(e.deployErrorCode);
export const HermesKeyState = z.enum(e.hermesKeyState);
export const HermesInstanceSource = z.enum(e.hermesInstanceSource);
export const SatelliteTransport = z.enum(e.satelliteTransport);
export const SatelliteStatus = z.enum(e.satelliteStatus);
export const RegistrySource = z.enum(e.registrySource);
export const AgentEndpointStatus = z.enum(e.agentEndpointStatus);
export const SatelliteReleaseComponent = z.enum(e.satelliteReleaseComponent);
export const SatelliteReleaseAction = z.enum(e.satelliteReleaseAction);
export const AgentSpecSource = z.enum(e.agentSpecSource);
export const AgentProfileSpecReportStatus = z.enum(e.agentProfileSpecReportStatus);
export const AgentSpecPart = z.enum(e.agentSpecPart);
export const DispatchPhase = z.enum(e.dispatchPhase);
export const AgentContainerStatus = z.enum(e.agentContainerStatus);
export const AgentSpecApplyStatus = z.enum(e.agentSpecApplyStatus);
export const AgentContainerHealth = z.enum(e.agentContainerHealth);
export const PanelProfileSource = z.enum(e.panelProfileSource);
export const RuntimeRefSource = z.enum(e.runtimeRefSource);
export const HermesProbeState = z.enum(e.hermesProbeState);
export const DeploySpendReaderState = z.enum(e.deploySpendReaderState);
export const DeployReconcileState = z.enum(e.deployReconcileState);
export const DeployCallbackType = z.enum(e.deployCallbackType);
export const PlatformAuditAction = z.enum(e.platformAuditAction);
export const ServiceEndpointKey = z.enum(e.serviceEndpointKey);
// WF-CONTROL
export const ProfileRevisionSubject = z.enum(e.profileRevisionSubject);
export const ProfileRevisionVia = z.enum(e.profileRevisionVia);
// agency console (M5-05)
export const BreakGlassState = z.enum(e.breakGlassState);
export const BreakGlassScope = z.enum(e.breakGlassScope);
export const BreakGlassReadSubjectKind = z.enum(e.breakGlassReadSubjectKind);
export const PortfolioProjectionKind = z.enum(e.portfolioProjectionKind);
export const Verdict = z.enum(e.verdict);
export const ModelPath = z.enum(e.modelPath);
export const ModelAccessMode = z.enum(e.modelAccessMode);
export const RunModelPath = z.enum(e.runModelPath);
export const RunDecisionResult = z.enum(e.runDecisionResult);
export const RunEscalationKind = z.enum(e.runEscalationKind);
export const DriftAuditTrigger = z.enum(e.driftAuditTrigger);
export const DriftAuditState = z.enum(e.driftAuditState);
export const ImportRunSource = z.enum(e.importRunSource);
export const ImportRunStatus = z.enum(e.importRunStatus);
export const ModelCredentialScope = z.enum(e.modelCredentialScope);
export const ModelCredentialKind = z.enum(e.modelCredentialKind);
export const CliLoginLocation = z.enum(e.cliLoginLocation);
export const ModelCredentialState = z.enum(e.modelCredentialState);
export const ToolConnectionScope = z.enum(e.toolConnectionScope);
export const ToolConnectionProvider = z.enum(e.toolConnectionProvider);
export const ToolConnectionState = z.enum(e.toolConnectionState);
export const CrewConnectionKind = z.enum(e.crewConnectionKind);
// SCREENS-S1
export const ScreenStackState = z.enum(e.screenStackState);
export const ScreenStackPlacement = z.enum(e.screenStackPlacement);
export const BrowserLiveMode = z.enum(e.browserLiveMode);
export const BrowserAccountStatus = z.enum(e.browserAccountStatus);
export const BrowserAccountKind = z.enum(e.browserAccountKind);
export const BrowserAccountGrantLevel = z.enum(e.browserAccountGrantLevel);
export const BrowserAccountGrantSubject = z.enum(e.browserAccountGrantSubject);
export const ScreenLeaseKind = z.enum(e.screenLeaseKind);
export const ScreenLeaseState = z.enum(e.screenLeaseState);
export const ScreenLeaseHolder = z.enum(e.screenLeaseHolder);
export const ScreenLeaseOrigin = z.enum(e.screenLeaseOrigin);
export const ScreenLeasePurpose = z.enum(e.screenLeasePurpose);
export const ScreenAskState = z.enum(e.screenAskState);
// BOARD-RESULTS
export const PostPublicationSource = z.enum(e.postPublicationSource);
// ONBOARDING-P1
export const OnboardingCaseStatus = z.enum(e.onboardingCaseStatus);
export const OnboardingDelivery = z.enum(e.onboardingDelivery);
export const OnboardingFollowupKind = z.enum(e.onboardingFollowupKind);
export const OnboardingFollowupStatus = z.enum(e.onboardingFollowupStatus);
export const OnboardingEventKind = z.enum(e.onboardingEventKind);
// ONBOARDING-P2
export const SetupProposalStatus = z.enum(e.setupProposalStatus);
export const SetupProposalOrigin = z.enum(e.setupProposalOrigin);
export const SetupSeatKind = z.enum(e.setupSeatKind);
export const SetupSeatStart = z.enum(e.setupSeatStart);
export const SetupSeatDecision = z.enum(e.setupSeatDecision);
export const AgentAssignmentRole = z.enum(e.agentAssignmentRole);
export const AgentAssignmentEndReason = z.enum(e.agentAssignmentEndReason);
export const SetupAssistantStatus = z.enum(e.setupAssistantStatus);
// ONBOARDING-P3
export const OnboardingSeatingStatus = z.enum(e.onboardingSeatingStatus);
export const OnboardingSeatingStep = z.enum(e.onboardingSeatingStep);
export const TemplateTicketStatus = z.enum(e.templateTicketStatus);
// lane A (beta shared contracts §3.2)
export const OnboardingReadinessCheck = z.enum(e.onboardingReadinessCheck);
// ONBOARDING-P4
export const DispatchItemSource = z.enum(e.dispatchItemSource);
export const DispatchItemClass = z.enum(e.dispatchItemClass);
export const DispatchItemRoute = z.enum(e.dispatchItemRoute);
export const DispatchQuarantineReason = z.enum(e.dispatchQuarantineReason);
export const OnboardingReviewKind = z.enum(e.onboardingReviewKind);
export const OnboardingReviewStatus = z.enum(e.onboardingReviewStatus);
// lane B: REQUEST-IDENTITY / REQUEST-RECOVERY
export const RequestOrigin = z.enum(e.requestOrigin);
export const RequestState = z.enum(e.requestState);
export const RequestDecidedVia = z.enum(e.requestDecidedVia);
export const RequestSubjectKind = z.enum(e.requestSubjectKind);
export const RecoveryCause = z.enum(e.recoveryCause);
export const RecoveryState = z.enum(e.recoveryState);
export const RecoveryAccountableVia = z.enum(e.recoveryAccountableVia);
export const RecoveryAction = z.enum(e.recoveryAction);
// AUTO-PROVISION
export const DeRuntimeOrderState = z.enum(e.deRuntimeOrderState);
export const DeRuntimeModelTier = z.enum(e.deRuntimeModelTier);
// MONITORING-P1
export const TelemetryLevel = z.enum(e.telemetryLevel);
export const MonitorJobStatus = z.enum(e.monitorJobStatus);
export const MonitorAlertKind = z.enum(e.monitorAlertKind);
export const MonitorAlertSeverity = z.enum(e.monitorAlertSeverity);
export const MonitorAlertState = z.enum(e.monitorAlertState);
export const ProbeCategory = z.enum(e.probeCategory);
export const ProbeFlagSource = z.enum(e.probeFlagSource);
export const RunStopRequestState = z.enum(e.runStopRequestState);
export const RunStopRequestSource = z.enum(e.runStopRequestSource);
export const GuardMatchKind = z.enum(e.guardMatchKind);
export const GuardAction = z.enum(e.guardAction);
// SKILL-LEAK-GUARD
export const SkillGuardMode = z.enum(e.skillGuardMode);
export const StructureChangeKind = z.enum(e.structureChangeKind);
export const StructureSubjectKind = z.enum(e.structureSubjectKind);
export const StructureAgentKind = z.enum(e.structureAgentKind);
export const ScreenLeaseEndReason = z.enum(e.screenLeaseEndReason);
export const ScreenNeedsYouWhy = z.enum(e.screenNeedsYouWhy);
export const ConnectionGranteeKind = z.enum(e.connectionGranteeKind);
export const ConnectionGrantLevel = z.enum(e.connectionGrantLevel);
export const ConnectionFamily = z.enum(e.connectionFamily);
export const ExternalActionKind = z.enum(e.externalActionKind);
export const OutboxStatus = z.enum(e.outboxStatus);
export const InboundSourceKind = z.enum(e.inboundSourceKind);
export const InboundRuleKind = z.enum(e.inboundRuleKind);
export const InboundEventStatus = z.enum(e.inboundEventStatus);
export const InboundHandlerVia = z.enum(e.inboundHandlerVia);
export const InboundVerification = z.enum(e.inboundVerification);
export const NotifyPreset = z.enum(e.notifyPreset);
export const IntegrationSubscriptionKind = z.enum(e.integrationSubscriptionKind);
export const IntegrationSubscriptionState = z.enum(e.integrationSubscriptionState);
export const IntegrationJobKind = z.enum(e.integrationJobKind);
export const IntegrationJobState = z.enum(e.integrationJobState);
export const IntegrationErrorClass = z.enum(e.integrationErrorClass);
// INC2-CONTACT
export const ContactLifecycle = z.enum(e.contactLifecycle);
export const ContactSource = z.enum(e.contactSource);
export const ContactIdentityKind = z.enum(e.contactIdentityKind);
export const ConsentChannel = z.enum(e.consentChannel);
export const ConsentStatus = z.enum(e.consentStatus);
export const ConsentBasis = z.enum(e.consentBasis);
export const ContactDuplicateState = z.enum(e.contactDuplicateState);

// cell: measurement
export const MetricUnit = z.enum(e.metricUnit);
export const MetricDirection = z.enum(e.metricDirection);
export const MetricAggregation = z.enum(e.metricAggregation);
export const MetricNormalization = z.enum(e.metricNormalization);
export const MetricCadence = z.enum(e.metricCadence);
export const MetricSource = z.enum(e.metricSource);
export const SpendSubjectKind = z.enum(e.spendSubjectKind);
export const TaskMetricSubjectKind = z.enum(e.taskMetricSubjectKind);
export const Visibility = z.enum(e.visibility);
export const GapPolicy = z.enum(e.gapPolicy);
export const TrackingMode = z.enum(e.trackingMode);
export const ReportingMethod = z.enum(e.reportingMethod);
export const ReportingRemind = z.enum(e.reportingRemind);
export const SubjectKind = z.enum(e.subjectKind);
export const TeamRollup = z.enum(e.teamRollup);
export const KpiPrincipalKind = z.enum(e.kpiPrincipalKind);
export const KpiScope = z.enum(e.kpiScope);
export const KpiGroup = z.enum(e.kpiGroup);
export const PeriodType = z.enum(e.periodType);
export const ObservationStatus = z.enum(e.observationStatus);
export const ObservationSource = z.enum(e.observationSource);
export const FridayReportState = z.enum(e.fridayReportState);

// cell: cost and time
export const CostEventKind = z.enum(e.costEventKind);
export const SubscriptionCadence = z.enum(e.subscriptionCadence);
export const TimeEntryKind = z.enum(e.timeEntryKind);
export const TimeEntryGrain = z.enum(e.timeEntryGrain);
export const TimeEntrySource = z.enum(e.timeEntrySource);
export const ApprovalStatus = z.enum(e.approvalStatus);

// cell: events
export const ActorKind = z.enum(e.actorKind);
export const ConsumerState = z.enum(e.consumerState);

// cell: memory and Recall (M7-03)
export const RecallSubjectKind = z.enum(e.recallSubjectKind);
export const RecallIssuanceState = z.enum(e.recallIssuanceState);
export const RecallMemoryCopyState = z.enum(e.recallMemoryCopyState);
export const RecallIngestBucketKind = z.enum(e.recallIngestBucketKind);
export const RecallKbAgentAccess = z.enum(e.recallKbAgentAccess);
export const RecallCaptureSource = z.enum(e.recallCaptureSource);
export const RecallCaptureLogSource = z.enum(e.recallCaptureLogSource);
export const RecallMirrorKind = z.enum(e.recallMirrorKind);
export const RecallCaptureOutcome = z.enum(e.recallCaptureOutcome);
export const MemoryRetentionState = z.enum(e.memoryRetentionState);
export const MemoryHoldScope = z.enum(e.memoryHoldScope);
export const MemoryHoldState = z.enum(e.memoryHoldState);
export const MemoryForgetMode = z.enum(e.memoryForgetMode);
export const MemoryForgetScope = z.enum(e.memoryForgetScope);
export const MemoryForgetState = z.enum(e.memoryForgetState);
export const OperatorJobKind = z.enum(e.operatorJobKind);
export const OperatorJobState = z.enum(e.operatorJobState);
export const OperatorSettingKey = z.enum(e.operatorSettingKey);
export type OperatorSettingKey = z.infer<typeof OperatorSettingKey>;
export const OperatorSettingScope = z.enum(e.operatorSettingScope);
export const OperatorAgentSlug = z.enum(e.operatorAgentSlug);
export const OperatorAgentStatus = z.enum(e.operatorAgentStatus);
export const AgentMemoryKind = z.enum(e.agentMemoryKind);
export const AgentMemoryEvidenceKind = z.enum(e.agentMemoryEvidenceKind);
export const AgentMemoryWriteState = z.enum(e.agentMemoryWriteState);
export const RecallOutboxTargetKind = z.enum(e.recallOutboxTargetKind);
export const RecallOutboxWriterKind = z.enum(e.recallOutboxWriterKind);
export const RecallOutboxState = z.enum(e.recallOutboxState);
export const MemoryScopeKind = z.enum(e.memoryScopeKind);
export const PromotionState = z.enum(e.promotionState);
export const MemoryProposalState = z.enum(e.memoryProposalState);
export const MemoryProposalDecidedHow = z.enum(e.memoryProposalDecidedHow);
export const MemoryContradictionState = z.enum(e.memoryContradictionState);
export const MemoryContradictionAction = z.enum(e.memoryContradictionAction);
export const PlanCheckVerdict = z.enum(e.planCheckVerdict);
export const MemoryLaneKind = z.enum(e.memoryLaneKind);
export const MemoryLaneAction = z.enum(e.memoryLaneAction);
export const MemoryLaneDecisionState = z.enum(e.memoryLaneDecisionState);
export const MemoryAdminActKind = z.enum(e.memoryAdminActKind);
export const MemoryAdminActState = z.enum(e.memoryAdminActState);
export const MemoryQualityScope = z.enum(e.memoryQualityScope);
export const MemoryQualityFindingKind = z.enum(e.memoryQualityFindingKind);
export const MemoryIngestSource = z.enum(e.memoryIngestSource);
export const MemoryQualityFindingSource = z.enum(e.memoryQualityFindingSource);
export const MemoryQualityFindingState = z.enum(e.memoryQualityFindingState);
export const MemoryArchiveSuggestionState = z.enum(e.memoryArchiveSuggestionState);
export const MemoryArchiveSuggestionSource = z.enum(e.memoryArchiveSuggestionSource);
export const DecisionOutcomeAnswer = z.enum(e.decisionOutcomeAnswer);
export const DecisionSource = z.enum(e.decisionSource);
export const RecallMemoryBlockKind = z.enum(e.recallMemoryBlockKind);
export const RecallMemoryBlockSubject = z.enum(e.recallMemoryBlockSubject);
export const RecallMemoryBlockState = z.enum(e.recallMemoryBlockState);
export const RecallActKind = z.enum(e.recallActKind);
export const RecallActState = z.enum(e.recallActState);
export const RecallRoleState = z.enum(e.recallRoleState);
export const RecallSuccessionState = z.enum(e.recallSuccessionState);
export const RecallProfileDraftState = z.enum(e.recallProfileDraftState);
export const RecallOnboardingDepth = z.enum(e.recallOnboardingDepth);
export const RecallOnboardingState = z.enum(e.recallOnboardingState);
export const RecallCharterSource = z.enum(e.recallCharterSource);
export const RecallCharterState = z.enum(e.recallCharterState);
export const RecallCharterSubject = z.enum(e.recallCharterSubject);
export const RecallKpiMirrorKind = z.enum(e.recallKpiMirrorKind);
export const RecallKpiMirrorState = z.enum(e.recallKpiMirrorState);
export const DecisionStanceKind = z.enum(e.decisionStanceKind);
export const DecisionStanceRecallState = z.enum(e.decisionStanceRecallState);
export const ReminderOrigin = z.enum(e.reminderOrigin);

// ── Parity P1 (task detail, layout, prefs, undo) ──
export const TaskLayoutScope = z.enum(e.taskLayoutScope);
export const FieldRequiredStage = z.enum(e.fieldRequiredOn);
// ── Parity P6a (policy, proposals, approvals) ──
export const AgentPolicyScope = z.enum(e.agentPolicyScope);
export const AgentPolicyMode = z.enum(e.agentPolicyMode);
export const AgentDecisionVerdict = z.enum(e.agentDecisionVerdict);
export const AgentDecisionStage = z.enum(e.agentDecisionStage);
export const AgentActionClass = z.enum(e.agentActionClass);
// ── CORE-FREEZE ──
export const IncidentKind = z.enum(e.incidentKind);
export const IncidentScope = z.enum(e.incidentScope);
export const IncidentStatus = z.enum(e.incidentStatus);
export const FreezeOutboxMode = z.enum(e.freezeOutboxMode);
export const RulingDecision = z.enum(e.rulingDecision);
export const MailHeaderDirection = z.enum(e.mailHeaderDirection);
// ── Parity P4 (lifecycle, access, structure) ──
export const AccessSubjectKind = z.enum(e.accessSubjectKind);
export const AccessRestrictableKind = z.enum(e.accessRestrictableKind);
export const AccessPrincipalKind = z.enum(e.accessPrincipalKind);
export const AccessGrantLevel = z.enum(e.accessGrantLevel);
export const AccessChange = z.enum(e.accessChange);
// ── Parity P5 (process engine) ──
export const AutomationScope = z.enum(e.automationScope);
export const AutomationSource = z.enum(e.automationSource);
export const AutomationPauseReason = z.enum(e.automationPauseReason);
export const AutomationRunOutcome = z.enum(e.automationRunOutcome);
export const TaskDueReminderKind = z.enum(e.taskDueReminderKind);
export const RecurringTakeoverScope = z.enum(e.recurringTakeoverScope);
export const RecurringRunOutcome = z.enum(e.recurringRunOutcome);
// ── CHAT-SWITCH-B ──
export const ChatAskState = z.enum(e.chatAskState);
export const ScheduledMessageState = z.enum(e.scheduledMessageState);
export const LinkPreviewStatus = z.enum(e.linkPreviewStatus);
export const ChatBroadcast = z.enum(e.chatBroadcast);
// ── COS-SLICE1 ──
export const SuggestionKind = z.enum(e.suggestionKind);
export const SuggestionState = z.enum(e.suggestionState);
export const SuggestionSource = z.enum(e.suggestionSource);
export const SuggestionResultKind = z.enum(e.suggestionResultKind);
export const CosDigestKind = z.enum(e.cosDigestKind);
// ── WALLET-P1 ──
// lane D
export const UsageCategory = z.enum(e.usageCategory);
// end lane D
export const WalletEntryKind = z.enum(e.walletEntryKind);
export const TariffScope = z.enum(e.tariffScope);
export const WalletCheckoutStatus = z.enum(e.walletCheckoutStatus);
export const WalletRefundIntentState = z.enum(e.walletRefundIntentState);
export const DePauseReason = z.enum(e.dePauseReason);
// ── MANAGED-POLICY ──
export const CompanyAgentMode = z.enum(e.companyAgentMode);
export const AgentRequestKind = z.enum(e.agentRequestKind);
export const AgentRequestState = z.enum(e.agentRequestState);
export const HelperRequestStage = z.enum(e.helperRequestStage);
export const AgentUseSource = z.enum(e.agentUseSource);
// TEMPLATES-T1..T4
export const ModelTier = z.enum(e.modelTier);
export const TemplateOverlayScope = z.enum(e.templateOverlayScope);
export const VerticalPackStatus = z.enum(e.verticalPackStatus);
export const AccessLevel = z.enum(e.accessLevel);
export const AccessDefaultSource = z.enum(e.accessDefaultSource);
export const AccessFamily = z.enum(e.accessFamily);
// ── REACH-S1 ──
export const AgentReachMemberKind = z.enum(e.agentReachMemberKind);
export const AgentReachServesKind = z.enum(e.agentReachServesKind);
export const AgentReachRequestState = z.enum(e.agentReachRequestState);
// ── SHARED-DES ──
export const SeatServesTargetKind = z.enum(e.seatServesTargetKind);
// ── MULTI-SPACE-ACCESS ──
export const SpaceShareObjectType = z.enum(e.spaceShareObjectType);
export const SpaceShareLevel = z.enum(e.spaceShareLevel);
// ── KB-UPLOAD-2 ──
export const PageMemberLevel = z.enum(e.pageMemberLevel);
export const PageMemberPrincipalKind = z.enum(e.pageMemberPrincipalKind);
// ── GUIDED-FLOW ──
export const FlowKey = z.enum(e.flowKey);
export const FlowRunStatus = z.enum(e.flowRunStatus);
export const SensitiveValueName = z.enum(e.sensitiveValueName);
// ── DE-INITIATE ──
/** The API contract names its own `AgentInitiateRule` in contracts/api/chat.ts. */
const AgentInitiateRuleEnum = z.enum(e.agentInitiateRule);
export const DeInitiationAboutKind = z.enum(e.deInitiationAboutKind);
export const DeInitiationTarget = z.enum(e.deInitiationTarget);
export const DeInitiationOutcome = z.enum(e.deInitiationOutcome);
export const DeInitiationRefusal = z.enum(e.deInitiationRefusal);
// ── DE-BRIEFS ──
export const BriefScope = z.enum(e.briefScope);
export const BriefFrequency = z.enum(e.briefFrequency);
export const DeBriefKind = z.enum(e.deBriefKind);
export const DeBriefState = z.enum(e.deBriefState);
export const DeBriefCorrectionApplied = z.enum(e.deBriefCorrectionApplied);
export const BriefRuleRecipient = z.enum(e.briefRuleRecipient);
export const BriefSection = z.enum(e.briefSection);
export const BriefChannel = z.enum(e.briefChannel);
// ── DE-BUDGET-PACE ──
export const DeBudgetAlertLevel = z.enum(e.deBudgetAlertLevel);
// ── SYSTEM-AGENT ──
export const SystemAgentFeedbackKind = z.enum(e.systemAgentFeedbackKind);
export const SystemAgentFeedbackSeverity = z.enum(e.systemAgentFeedbackSeverity);
export const SystemAgentFixStatus = z.enum(e.systemAgentFixStatus);
export const OperatorInboxKind = z.enum(e.operatorInboxKind);
export const AgentIssueSource = z.enum(e.agentIssueSource);
export const AgentIssueKind = z.enum(e.agentIssueKind);
export const AgentIssueStatus = z.enum(e.agentIssueStatus);
export const SystemAgentMessageAuthor = z.enum(e.systemAgentMessageAuthor);
export const SystemAgentMessageKind = z.enum(e.systemAgentMessageKind);
export const SystemAgentKeyScope = z.enum(e.systemAgentKeyScope);
export const SystemAgentKeyStatus = z.enum(e.systemAgentKeyStatus);
// ── PROVISION-IN-PRODUCT ──
export const ProvisioningSettingKey = z.enum(e.provisioningSettingKey);
export const ProvisioningStepState = z.enum(e.provisioningStepState);
export const ProvisioningOrigin = z.enum(e.provisioningOrigin);
export const AigentpanelNoncePurpose = z.enum(e.aigentpanelNoncePurpose);
// ── SKILLSMITH-OPPORTUNITIES ──
export const OpportunitySource = z.enum(e.opportunitySource);
export const OpportunityStage = z.enum(e.opportunityStage);
export const OpportunitySuggestionKind = z.enum(e.opportunitySuggestionKind);
export const OpportunityConfidence = z.enum(e.opportunityConfidence);
export const HelperOfferStatus = z.enum(e.helperOfferStatus);
export const HelperChargeStatus = z.enum(e.helperChargeStatus);
// ── ADMIN-INSIGHTS ──
export const InsightMeasure = z.enum(e.insightMeasure);
export const InsightBy = z.enum(e.insightBy);
export const InsightPeriod = z.enum(e.insightPeriod);
export const InsightViz = z.enum(e.insightViz);
export const InsightVisibility = z.enum(e.insightVisibility);
export const InsightAskVia = z.enum(e.insightAskVia);
// ── BIZ-PLAYBOOKS / BIZ-CLIENT-HEALTH ──
export const PlaybookLinkKind = z.enum(e.playbookLinkKind);
export const ClientSurveyKind = z.enum(e.clientSurveyKind);
export const ClientHealthBand = z.enum(e.clientHealthBand);
// ── RECALL-RULES / RECALL-PROCEDURES ──
export const MemoryRuleScope = z.enum(e.memoryRuleScope);
export const MemoryRuleStatus = z.enum(e.memoryRuleStatus);
export const MemoryRuleSource = z.enum(e.memoryRuleSource);
export const RecallPushState = z.enum(e.recallPushState);
export const ProcedureRunResult = z.enum(e.procedureRunResult);
// ── SKILL-PROPOSALS ──
export const SkillProposalStatus = z.enum(e.skillProposalStatus);
export const SkillProposalRecommendation = z.enum(e.skillProposalRecommendation);
export const SkillProposalSource = z.enum(e.skillProposalSource);
export const SkillProposalConsent = z.enum(e.skillProposalConsent);
export const NightlyReviewStatus = z.enum(e.nightlyReviewStatus);
// ── SKILLSMITH-LENS ──
export const FleetReportKind = z.enum(e.fleetReportKind);
export const FleetReportNarrative = z.enum(e.fleetReportNarrative);
export const SkillsmithOrderKind = z.enum(e.skillsmithOrderKind);
export const SkillsmithOrderStatus = z.enum(e.skillsmithOrderStatus);
// SKILLSMITH-CONTROL
export const SkillsmithCadence = z.enum(e.skillsmithCadence);
export const SkillsmithStandingStatus = z.enum(e.skillsmithStandingStatus);
export const SkillsmithDraftKind = z.enum(e.skillsmithDraftKind);
export const SkillsmithDraftState = z.enum(e.skillsmithDraftState);
export const SkillsmithActionKind = z.enum(e.skillsmithActionKind);
export type SkillsmithActionKind = z.infer<typeof SkillsmithActionKind>;
export const ServiceErrorService = z.enum(e.serviceErrorService);
export const SatelliteIncidentKind = z.enum(e.satelliteIncidentKind);
export const SatelliteIncidentSeverity = z.enum(e.satelliteIncidentSeverity);
// ── BILLING-PLANS ──
export const MaintenanceTier = z.enum(e.maintenanceTier);
export const MaintenanceSupervision = z.enum(e.maintenanceSupervision);
export const CollectionMethod = z.enum(e.collectionMethod);
export const SystemUsageBilling = z.enum(e.systemUsageBilling).meta({ id: "SystemUsageBilling" });
export const InvoicePaymentMethod = z.enum(e.invoicePaymentMethod);
export const SubscriptionStatus = z.enum(e.subscriptionStatus);
export const InvoiceStatus = z.enum(e.invoiceStatus);
export const InvoiceSource = z.enum(e.invoiceSource);
export const DiscountAppliesTo = z.enum(e.discountAppliesTo);
export const QuoteStatus = z.enum(e.quoteStatus);
export const BillingOperationStatus = z.enum(e.billingOperationStatus);
export const ChargeKind = z.enum(e.chargeKind);
export const BuildProjectKind = z.enum(e.buildProjectKind);
export const BuildProjectStatus = z.enum(e.buildProjectStatus);
export const BuildMaintenanceShape = z.enum(e.buildMaintenanceShape);
export const MilestoneStatus = z.enum(e.milestoneStatus);

/** Every database enum by its tuple name in `src/enums.ts` (used by the drift tests). */
export const databaseEnums = {
  agencyGrantLevel: AgencyGrantLevel,
  agencyUserRole: AgencyUserRole,
  billingInterval: BillingInterval,
  addonKey: AddonKey,
  supportedCurrency: Currency,
  addonDependencyMode: AddonDependencyMode,
  entitlementStatus: EntitlementStatus,
  entitlementSource: EntitlementSource,
  companyKind: CompanyKind,
  membershipKind: MembershipKind,
  seatHolderKind: SeatHolderKind,
  membershipStatus: MembershipStatus,
  invitationStatus: InvitationStatus,
  invitationRole: InvitationRole,
  seatKind: SeatKind,
  seatStatus: SeatStatus,
  seatOrigin: SeatOrigin,
  seatDeFill: SeatDeFill,
  seatAssignmentCapacity: SeatAssignmentCapacity,
  digitalEmployeeStatus: DigitalEmployeeStatus,
  workforceAgentStatus: WorkforceAgentStatus,
  workforceAgentReportsToKind: WorkforceAgentReportsToKind,
  agentKeyKind: AgentKeyKind,
  agentKeyStatus: AgentKeyStatus,
  agentKeyScope: AgentKeyScope,
  ingestKeyScope: IngestKeyScope,
  sourceKeyScope: SourceKeyScope,
  ingestKeySource: IngestKeySource,
  meterSettlementResolution: MeterSettlementResolution,
  externalIdentityKind: ExternalIdentityKind,
  teamComposition: TeamComposition,
  seatEdgeKind: SeatEdgeKind,
  responsibilityStatus: ResponsibilityStatus,
  recurringWorkStatus: RecurringWorkStatus,
  goalKind: GoalKind,
  goalState: GoalState,
  goalTrack: GoalTrack,
  assessmentQuestionnaire: AssessmentQuestionnaire,
  assessmentRoundState: AssessmentRoundState,
  assessmentParticipantKind: AssessmentParticipantKind,
  assessmentParticipantState: AssessmentParticipantState,
  assessmentProposalKind: AssessmentProposalKind,
  assessmentProposalState: AssessmentProposalState,
  issueStatus: IssueStatus,
  issueResolutionKind: IssueResolutionKind,
  issueEscalation: IssueEscalation,
  issueSourceKind: IssueSourceKind,
  meetingSeriesKind: MeetingSeriesKind,
  meetingState: MeetingState,
  meetingPrereadState: MeetingPrereadState,
  agendaSection: AgendaSection,
  agendaSourceKind: AgendaSourceKind,
  agendaOutcomeKind: AgendaOutcomeKind,
  meetingOutcomeKind: MeetingOutcomeKind,
  calendarKind: CalendarKind,
  calendarVisibility: CalendarVisibility,
  calendarEventStatus: CalendarEventStatus,
  calendarEventVisibility: CalendarEventVisibility,
  calendarEventSource: CalendarEventSource,
  calendarAttendeeResponse: CalendarAttendeeResponse,
  calendarShareLevel: CalendarShareLevel,
  calendarShareGrantee: CalendarShareGrantee,
  calendarRevisionReason: CalendarRevisionReason,
  webhookStatus: WebhookStatus,
  webhookDeliveryStatus: WebhookDeliveryStatus,
  inboundWebhookVerification: InboundWebhookVerification,
  inboundWebhookStatus: InboundWebhookStatus,
  inboundWebhookScreenMode: InboundWebhookScreenMode,
  inboundDeliveryStatus: InboundDeliveryStatus,
  inboundDeliveryVerification: InboundDeliveryVerification,
  accountGrantLevel: AccountGrantLevel,
  accountCompanyRelation: AccountCompanyRelation,
  founderBriefCadence: FounderBriefCadence,
  founderBriefSource: FounderBriefSource,
  spaceKind: SpaceKind,
  channelKind: ChannelKind,
  channelMemberRole: ChannelMemberRole,
  channelPostingPolicy: ChannelPostingPolicy,
  threadParticipantRole: ThreadParticipantRole,
  channelNotifyLevel: ChannelNotifyLevel,
  messageAuthorKind: MessageAuthorKind,
  agentWakeRule: AgentWakeRuleEnum,
  topicStatus: TopicStatus,
  topicMemberVia: TopicMemberVia,
  threadKind: ThreadKind,
  attachmentSubjectKind: AttachmentSubjectKind,
  attachmentState: AttachmentState,
  pageUploadStatus: PageUploadStatus,
  followSubjectKind: FollowSubjectKind,
  followLevel: FollowLevel,
  inboxItemKind: InboxItemKind,
  inboxSubjectKind: InboxSubjectKind,
  digestFrequency: DigestFrequency,
  notificationCategory: NotificationCategory,
  notificationPreset: NotificationPreset,
  replyLength: ReplyLength,
  notificationOverrideLevel: NotificationOverrideLevel,
  searchHitKind: SearchHitKind,
  boardDigestInterval: BoardDigestInterval,
  boardColor: BoardColor,
  fieldType: FieldType,
  fieldAudience: FieldAudience,
  viewKind: ViewKind,
  taskOrigin: TaskOrigin,
  taskDependencyKind: TaskDependencyKind,
  tagColor: TagColor,
  taskLinkKind: TaskLinkKind,
  taskLinkRelation: TaskLinkRelation,
  favoriteKind: FavoriteKind,
  recentKind: RecentKind,
  myTaskBuiltinSection: MyTaskBuiltinSection,
  pageKind: PageKind,
  knowledgeSourceKind: KnowledgeSourceKind,
  agentTemplateScope: AgentTemplateScope,
  agentTemplateKind: AgentTemplateKind,
  agentTemplateStatus: AgentTemplateStatus,
  skillPackScope: SkillPackScope,
  skillPackStatus: SkillPackStatus,
  skillPackFormat: SkillPackFormat,
  skillAdditionVia: SkillAdditionVia,
  agentProfileOwnerKind: AgentProfileOwnerKind,
  agentProfileRole: AgentProfileRole,
  agentProfileState: AgentProfileState,
  wakeupKind: WakeupKind,
  boardEventClass: BoardEventClass,
  decisionBand: DecisionBand,
  decisionProvider: DecisionProvider,
  classifierDecisionSource: ClassifierDecisionSource,
  gateMode: GateMode,
  gateHoldState: GateHoldState,
  workerRefusalReason: WorkerRefusalReason,
  wakeupStatus: WakeupStatus,
  wakeupSteer: WakeupSteer,
  wakeupHoldReason: WakeupHoldReason,
  runEffortRequest: RunEffortRequest,
  runEffortTier: RunEffortTier,
  runEffortReasoning: RunEffortReasoning,
  runEffortSource: RunEffortSource,
  wakeTimerStatus: WakeTimerStatus,
  helperChainState: HelperChainState,
  helperChainSubjectKind: HelperChainSubjectKind,
  settledSubjectKind: SettledSubjectKind,
  settledSubjectHow: SettledSubjectHow,
  helperCardState: HelperCardState,
  helperCardKind: HelperCardKind,
  runHandoffKind: RunHandoffKind,
  commitmentState: CommitmentState,
  commitmentKind: CommitmentKind,
  commitmentActorKind: CommitmentActorKind,
  commitmentVerify: CommitmentVerify,
  blockerCategory: BlockerCategory,
  commitmentCause: CommitmentCause,
  commitmentSubjectKind: CommitmentSubjectKind,
  commitmentLogActorKind: CommitmentLogActorKind,
  loopIncidentKind: LoopIncidentKind,
  loopIncidentStatus: LoopIncidentStatus,
  runState: RunState,
  runRetryState: RunRetryState,
  runIdentityMode: RunIdentityMode,
  sandboxKind: SandboxKind,
  runStepKind: RunStepKind,
  canvasOpVerb: CanvasOpVerb,
  canvasBlockKind: CanvasBlockKind,
  liveView: LiveView,
  canvasInteraction: CanvasInteraction,
  approvalKind: ApprovalKind,
  approvalState: ApprovalState,
  approvalExecutionState: ApprovalExecutionState,
  approvalExecutionFailure: ApprovalExecutionFailure,
  approvalExecutionCode: ApprovalExecutionCode,
  delegationState: DelegationState,
  budgetSubjectKind: BudgetSubjectKind,
  budgetState: BudgetState,
  budgetPeriodType: BudgetPeriodType,
  reservationState: ReservationState,
  budgetIncidentKind: BudgetIncidentKind,
  priceListScope: PriceListScope,
  priceUnit: PriceUnit,
  personalCrewState: PersonalCrewState,
  personalCrewPauseReason: PersonalCrewPauseReason,
  crewAgentSource: CrewAgentSource,
  autonomyMode: AutonomyMode,
  crewActionKind: CrewActionKind,
  sharingRule: SharingRule,
  sharingSubjectKind: SharingSubjectKind,
  crewContextLayer: CrewContextLayer,
  crewContextVisibility: CrewContextVisibility,
  crewCapPeriod: CrewCapPeriod,
  agentRoomTurnPolicy: AgentRoomTurnPolicy,
  agentRoomParticipantKind: AgentRoomParticipantKind,
  statusAskSubjectKind: StatusAskSubjectKind,
  statusAskState: StatusAskState,
  statusAskEscalation: StatusAskEscalation,
  crewProposalKind: CrewProposalKind,
  crewProposalState: CrewProposalState,
  crewProposalSource: CrewProposalSource,
  crewMemoryBucket: CrewMemoryBucket,
  crewMemorySubjectKind: CrewMemorySubjectKind,
  financeRecordStatus: FinanceRecordStatus,
  revenueKind: RevenueKind,
  compensationCadence: CompensationCadence,
  recurringExpenseKind: RecurringExpenseKind,
  recurringCadence: RecurringCadence,
  categoryKind: CategoryKind,
  headcountHolderKind: HeadcountHolderKind,
  forecastSubjectKind: ForecastSubjectKind,
  financeRelation: FinanceRelation,
  templateVersionState: TemplateVersionState,
  templateVersionSource: TemplateVersionSource,
  templateClass: TemplateClass,
  templateEvalKind: TemplateEvalKind,
  templateEvalResult: TemplateEvalResult,
  enforcerKind: EnforcerKind,
  templateEditSource: TemplateEditSource,
  templateEditState: TemplateEditState,
  verificationState: VerificationState,
  deployPath: DeployPath,
  deployKind: DeployKind,
  deployState: DeployState,
  deployErrorCode: DeployErrorCode,
  hermesKeyState: HermesKeyState,
  hermesInstanceSource: HermesInstanceSource,
  satelliteTransport: SatelliteTransport,
  satelliteStatus: SatelliteStatus,
  registrySource: RegistrySource,
  agentEndpointStatus: AgentEndpointStatus,
  satelliteReleaseComponent: SatelliteReleaseComponent,
  satelliteReleaseAction: SatelliteReleaseAction,
  agentSpecSource: AgentSpecSource,
  agentProfileSpecReportStatus: AgentProfileSpecReportStatus,
  agentSpecPart: AgentSpecPart,
  dispatchPhase: DispatchPhase,
  agentContainerStatus: AgentContainerStatus,
  agentSpecApplyStatus: AgentSpecApplyStatus,
  agentContainerHealth: AgentContainerHealth,
  panelProfileSource: PanelProfileSource,
  runtimeRefSource: RuntimeRefSource,
  hermesProbeState: HermesProbeState,
  deploySpendReaderState: DeploySpendReaderState,
  deployReconcileState: DeployReconcileState,
  deployCallbackType: DeployCallbackType,
  platformAuditAction: PlatformAuditAction,
  serviceEndpointKey: ServiceEndpointKey,
  profileRevisionSubject: ProfileRevisionSubject,
  profileRevisionVia: ProfileRevisionVia,
  breakGlassState: BreakGlassState,
  breakGlassScope: BreakGlassScope,
  breakGlassReadSubjectKind: BreakGlassReadSubjectKind,
  portfolioProjectionKind: PortfolioProjectionKind,
  verdict: Verdict,
  modelPath: ModelPath,
  modelAccessMode: ModelAccessMode,
  runModelPath: RunModelPath,
  runDecisionResult: RunDecisionResult,
  runEscalationKind: RunEscalationKind,
  driftAuditTrigger: DriftAuditTrigger,
  driftAuditState: DriftAuditState,
  importRunSource: ImportRunSource,
  importRunStatus: ImportRunStatus,
  modelCredentialScope: ModelCredentialScope,
  modelCredentialKind: ModelCredentialKind,
  cliLoginLocation: CliLoginLocation,
  modelCredentialState: ModelCredentialState,
  toolConnectionScope: ToolConnectionScope,
  toolConnectionProvider: ToolConnectionProvider,
  toolConnectionState: ToolConnectionState,
  crewConnectionKind: CrewConnectionKind,
  screenStackState: ScreenStackState,
  screenStackPlacement: ScreenStackPlacement,
  browserLiveMode: BrowserLiveMode,
  browserAccountStatus: BrowserAccountStatus,
  browserAccountKind: BrowserAccountKind,
  browserAccountGrantLevel: BrowserAccountGrantLevel,
  browserAccountGrantSubject: BrowserAccountGrantSubject,
  screenLeaseKind: ScreenLeaseKind,
  screenLeaseState: ScreenLeaseState,
  screenLeaseHolder: ScreenLeaseHolder,
  screenLeaseOrigin: ScreenLeaseOrigin,
  screenLeasePurpose: ScreenLeasePurpose,
  screenAskState: ScreenAskState,
  postPublicationSource: PostPublicationSource,
  onboardingCaseStatus: OnboardingCaseStatus,
  onboardingDelivery: OnboardingDelivery,
  onboardingFollowupKind: OnboardingFollowupKind,
  onboardingFollowupStatus: OnboardingFollowupStatus,
  onboardingEventKind: OnboardingEventKind,
  setupProposalStatus: SetupProposalStatus,
  setupProposalOrigin: SetupProposalOrigin,
  setupSeatKind: SetupSeatKind,
  setupSeatStart: SetupSeatStart,
  setupSeatDecision: SetupSeatDecision,
  agentAssignmentRole: AgentAssignmentRole,
  agentAssignmentEndReason: AgentAssignmentEndReason,
  setupAssistantStatus: SetupAssistantStatus,
  onboardingSeatingStatus: OnboardingSeatingStatus,
  onboardingSeatingStep: OnboardingSeatingStep,
  templateTicketStatus: TemplateTicketStatus,
  onboardingReadinessCheck: OnboardingReadinessCheck,
  dispatchItemSource: DispatchItemSource,
  dispatchItemClass: DispatchItemClass,
  dispatchItemRoute: DispatchItemRoute,
  dispatchQuarantineReason: DispatchQuarantineReason,
  onboardingReviewKind: OnboardingReviewKind,
  onboardingReviewStatus: OnboardingReviewStatus,
  requestOrigin: RequestOrigin,
  requestState: RequestState,
  requestDecidedVia: RequestDecidedVia,
  requestSubjectKind: RequestSubjectKind,
  recoveryCause: RecoveryCause,
  recoveryState: RecoveryState,
  recoveryAccountableVia: RecoveryAccountableVia,
  recoveryAction: RecoveryAction,
  deRuntimeOrderState: DeRuntimeOrderState,
  deRuntimeModelTier: DeRuntimeModelTier,
  telemetryLevel: TelemetryLevel,
  monitorJobStatus: MonitorJobStatus,
  monitorAlertKind: MonitorAlertKind,
  monitorAlertSeverity: MonitorAlertSeverity,
  monitorAlertState: MonitorAlertState,
  probeCategory: ProbeCategory,
  probeFlagSource: ProbeFlagSource,
  runStopRequestState: RunStopRequestState,
  runStopRequestSource: RunStopRequestSource,
  guardMatchKind: GuardMatchKind,
  guardAction: GuardAction,
  skillGuardMode: SkillGuardMode,
  structureChangeKind: StructureChangeKind,
  structureSubjectKind: StructureSubjectKind,
  structureAgentKind: StructureAgentKind,
  screenLeaseEndReason: ScreenLeaseEndReason,
  screenNeedsYouWhy: ScreenNeedsYouWhy,
  connectionGranteeKind: ConnectionGranteeKind,
  connectionGrantLevel: ConnectionGrantLevel,
  connectionFamily: ConnectionFamily,
  externalActionKind: ExternalActionKind,
  outboxStatus: OutboxStatus,
  inboundSourceKind: InboundSourceKind,
  inboundRuleKind: InboundRuleKind,
  inboundEventStatus: InboundEventStatus,
  inboundHandlerVia: InboundHandlerVia,
  inboundVerification: InboundVerification,
  notifyPreset: NotifyPreset,
  integrationSubscriptionKind: IntegrationSubscriptionKind,
  integrationSubscriptionState: IntegrationSubscriptionState,
  integrationJobKind: IntegrationJobKind,
  integrationJobState: IntegrationJobState,
  integrationErrorClass: IntegrationErrorClass,
  contactLifecycle: ContactLifecycle,
  contactSource: ContactSource,
  contactIdentityKind: ContactIdentityKind,
  consentChannel: ConsentChannel,
  consentStatus: ConsentStatus,
  consentBasis: ConsentBasis,
  contactDuplicateState: ContactDuplicateState,
  metricUnit: MetricUnit,
  metricDirection: MetricDirection,
  metricAggregation: MetricAggregation,
  metricNormalization: MetricNormalization,
  metricCadence: MetricCadence,
  metricSource: MetricSource,
  spendSubjectKind: SpendSubjectKind,
  taskMetricSubjectKind: TaskMetricSubjectKind,
  visibility: Visibility,
  gapPolicy: GapPolicy,
  trackingMode: TrackingMode,
  reportingMethod: ReportingMethod,
  reportingRemind: ReportingRemind,
  subjectKind: SubjectKind,
  teamRollup: TeamRollup,
  kpiPrincipalKind: KpiPrincipalKind,
  kpiScope: KpiScope,
  kpiGroup: KpiGroup,
  periodType: PeriodType,
  observationStatus: ObservationStatus,
  observationSource: ObservationSource,
  fridayReportState: FridayReportState,
  costEventKind: CostEventKind,
  subscriptionCadence: SubscriptionCadence,
  timeEntryKind: TimeEntryKind,
  timeEntryGrain: TimeEntryGrain,
  timeEntrySource: TimeEntrySource,
  approvalStatus: ApprovalStatus,
  actorKind: ActorKind,
  consumerState: ConsumerState,
  recallSubjectKind: RecallSubjectKind,
  recallIssuanceState: RecallIssuanceState,
  recallMemoryCopyState: RecallMemoryCopyState,
  recallIngestBucketKind: RecallIngestBucketKind,
  recallKbAgentAccess: RecallKbAgentAccess,
  recallCaptureSource: RecallCaptureSource,
  recallCaptureLogSource: RecallCaptureLogSource,
  recallMirrorKind: RecallMirrorKind,
  recallCaptureOutcome: RecallCaptureOutcome,
  memoryRetentionState: MemoryRetentionState,
  memoryHoldScope: MemoryHoldScope,
  memoryHoldState: MemoryHoldState,
  memoryForgetMode: MemoryForgetMode,
  memoryForgetScope: MemoryForgetScope,
  memoryForgetState: MemoryForgetState,
  operatorJobKind: OperatorJobKind,
  operatorJobState: OperatorJobState,
  operatorSettingKey: OperatorSettingKey,
  operatorSettingScope: OperatorSettingScope,
  operatorAgentSlug: OperatorAgentSlug,
  operatorAgentStatus: OperatorAgentStatus,
  agentMemoryKind: AgentMemoryKind,
  agentMemoryEvidenceKind: AgentMemoryEvidenceKind,
  agentMemoryWriteState: AgentMemoryWriteState,
  recallOutboxTargetKind: RecallOutboxTargetKind,
  recallOutboxWriterKind: RecallOutboxWriterKind,
  recallOutboxState: RecallOutboxState,
  memoryScopeKind: MemoryScopeKind,
  promotionState: PromotionState,
  memoryProposalState: MemoryProposalState,
  memoryProposalDecidedHow: MemoryProposalDecidedHow,
  memoryContradictionState: MemoryContradictionState,
  memoryContradictionAction: MemoryContradictionAction,
  planCheckVerdict: PlanCheckVerdict,
  memoryLaneKind: MemoryLaneKind,
  memoryLaneAction: MemoryLaneAction,
  memoryLaneDecisionState: MemoryLaneDecisionState,
  memoryAdminActKind: MemoryAdminActKind,
  memoryAdminActState: MemoryAdminActState,
  memoryQualityScope: MemoryQualityScope,
  memoryQualityFindingKind: MemoryQualityFindingKind,
  memoryIngestSource: MemoryIngestSource,
  memoryQualityFindingSource: MemoryQualityFindingSource,
  memoryQualityFindingState: MemoryQualityFindingState,
  memoryArchiveSuggestionState: MemoryArchiveSuggestionState,
  memoryArchiveSuggestionSource: MemoryArchiveSuggestionSource,
  decisionOutcomeAnswer: DecisionOutcomeAnswer,
  decisionSource: DecisionSource,
  recallMemoryBlockKind: RecallMemoryBlockKind,
  recallMemoryBlockSubject: RecallMemoryBlockSubject,
  recallMemoryBlockState: RecallMemoryBlockState,
  recallActKind: RecallActKind,
  recallActState: RecallActState,
  recallRoleState: RecallRoleState,
  recallSuccessionState: RecallSuccessionState,
  recallProfileDraftState: RecallProfileDraftState,
  recallOnboardingDepth: RecallOnboardingDepth,
  recallOnboardingState: RecallOnboardingState,
  recallCharterSource: RecallCharterSource,
  recallCharterState: RecallCharterState,
  recallCharterSubject: RecallCharterSubject,
  recallKpiMirrorKind: RecallKpiMirrorKind,
  recallKpiMirrorState: RecallKpiMirrorState,
  decisionStanceKind: DecisionStanceKind,
  decisionStanceRecallState: DecisionStanceRecallState,
  reminderOrigin: ReminderOrigin,
  // Parity P1
  taskLayoutScope: TaskLayoutScope,
  fieldRequiredOn: FieldRequiredStage,
  // Parity P6a
  agentPolicyScope: AgentPolicyScope,
  agentPolicyMode: AgentPolicyMode,
  agentDecisionVerdict: AgentDecisionVerdict,
  agentDecisionStage: AgentDecisionStage,
  agentActionClass: AgentActionClass,
  // CORE-FREEZE
  incidentKind: IncidentKind,
  incidentScope: IncidentScope,
  incidentStatus: IncidentStatus,
  freezeOutboxMode: FreezeOutboxMode,
  rulingDecision: RulingDecision,
  mailHeaderDirection: MailHeaderDirection,
  // ── Parity P4 (lifecycle, access, structure) ──
  accessSubjectKind: AccessSubjectKind,
  accessRestrictableKind: AccessRestrictableKind,
  accessPrincipalKind: AccessPrincipalKind,
  accessGrantLevel: AccessGrantLevel,
  accessChange: AccessChange,
  // ── Parity P5 (process engine) ──
  automationScope: AutomationScope,
  automationSource: AutomationSource,
  automationPauseReason: AutomationPauseReason,
  automationRunOutcome: AutomationRunOutcome,
  taskDueReminderKind: TaskDueReminderKind,
  recurringTakeoverScope: RecurringTakeoverScope,
  recurringRunOutcome: RecurringRunOutcome,
  // ── CHAT-SWITCH-B ──
  chatAskState: ChatAskState,
  scheduledMessageState: ScheduledMessageState,
  linkPreviewStatus: LinkPreviewStatus,
  chatBroadcast: ChatBroadcast,
  // ── COS-SLICE1 ──
  suggestionKind: SuggestionKind,
  suggestionState: SuggestionState,
  suggestionSource: SuggestionSource,
  suggestionResultKind: SuggestionResultKind,
  cosDigestKind: CosDigestKind,
  // ── WALLET-P1 ──
  usageCategory: UsageCategory, // lane D
  walletEntryKind: WalletEntryKind,
  tariffScope: TariffScope,
  walletCheckoutStatus: WalletCheckoutStatus,
  walletRefundIntentState: WalletRefundIntentState,
  dePauseReason: DePauseReason,
  // ── MANAGED-POLICY ──
  companyAgentMode: CompanyAgentMode,
  agentRequestKind: AgentRequestKind,
  agentRequestState: AgentRequestState,
  helperRequestStage: HelperRequestStage,
  agentUseSource: AgentUseSource,
  // ── TEMPLATES-T1..T4 ──
  modelTier: ModelTier,
  templateOverlayScope: TemplateOverlayScope,
  verticalPackStatus: VerticalPackStatus,
  accessLevel: AccessLevel,
  accessDefaultSource: AccessDefaultSource,
  accessFamily: AccessFamily,
  // ── REACH-S1 ──
  agentReachMemberKind: AgentReachMemberKind,
  agentReachServesKind: AgentReachServesKind,
  agentReachRequestState: AgentReachRequestState,
  // ── SHARED-DES ──
  seatServesTargetKind: SeatServesTargetKind,
  // ── MULTI-SPACE-ACCESS ──
  spaceShareObjectType: SpaceShareObjectType,
  spaceShareLevel: SpaceShareLevel,
  // ── KB-UPLOAD-2 ──
  pageMemberLevel: PageMemberLevel,
  pageMemberPrincipalKind: PageMemberPrincipalKind,
  // ── GUIDED-FLOW ──
  flowKey: FlowKey,
  flowRunStatus: FlowRunStatus,
  sensitiveValueName: SensitiveValueName,
  // ── DE-INITIATE ──
  agentInitiateRule: AgentInitiateRuleEnum,
  deInitiationAboutKind: DeInitiationAboutKind,
  deInitiationTarget: DeInitiationTarget,
  deInitiationOutcome: DeInitiationOutcome,
  deInitiationRefusal: DeInitiationRefusal,
  // ── DE-BRIEFS ──
  briefScope: BriefScope,
  briefFrequency: BriefFrequency,
  deBriefKind: DeBriefKind,
  deBriefState: DeBriefState,
  deBriefCorrectionApplied: DeBriefCorrectionApplied,
  briefRuleRecipient: BriefRuleRecipient,
  briefSection: BriefSection,
  briefChannel: BriefChannel,
  // ── DE-BUDGET-PACE ──
  deBudgetAlertLevel: DeBudgetAlertLevel,
  // ── SYSTEM-AGENT ──
  systemAgentFeedbackKind: SystemAgentFeedbackKind,
  systemAgentFeedbackSeverity: SystemAgentFeedbackSeverity,
  systemAgentFixStatus: SystemAgentFixStatus,
  operatorInboxKind: OperatorInboxKind,
  agentIssueSource: AgentIssueSource,
  agentIssueKind: AgentIssueKind,
  agentIssueStatus: AgentIssueStatus,
  systemAgentMessageAuthor: SystemAgentMessageAuthor,
  systemAgentMessageKind: SystemAgentMessageKind,
  systemAgentKeyScope: SystemAgentKeyScope,
  systemAgentKeyStatus: SystemAgentKeyStatus,
  provisioningSettingKey: ProvisioningSettingKey,
  provisioningStepState: ProvisioningStepState,
  provisioningOrigin: ProvisioningOrigin,
  aigentpanelNoncePurpose: AigentpanelNoncePurpose,
  // ── SKILLSMITH-OPPORTUNITIES ──
  opportunitySource: OpportunitySource,
  opportunityStage: OpportunityStage,
  opportunitySuggestionKind: OpportunitySuggestionKind,
  opportunityConfidence: OpportunityConfidence,
  helperOfferStatus: HelperOfferStatus,
  helperChargeStatus: HelperChargeStatus,
  // ── ADMIN-INSIGHTS ──
  insightMeasure: InsightMeasure,
  insightBy: InsightBy,
  insightPeriod: InsightPeriod,
  insightViz: InsightViz,
  insightVisibility: InsightVisibility,
  insightAskVia: InsightAskVia,
  // ── BIZ-PLAYBOOKS / BIZ-CLIENT-HEALTH ──
  playbookLinkKind: PlaybookLinkKind,
  clientSurveyKind: ClientSurveyKind,
  clientHealthBand: ClientHealthBand,
  // ── RECALL-RULES / RECALL-PROCEDURES ──
  memoryRuleScope: MemoryRuleScope,
  memoryRuleStatus: MemoryRuleStatus,
  memoryRuleSource: MemoryRuleSource,
  recallPushState: RecallPushState,
  procedureRunResult: ProcedureRunResult,
  // ── SKILL-PROPOSALS ──
  skillProposalStatus: SkillProposalStatus,
  skillProposalRecommendation: SkillProposalRecommendation,
  skillProposalSource: SkillProposalSource,
  skillProposalConsent: SkillProposalConsent,
  nightlyReviewStatus: NightlyReviewStatus,
  // ── SKILLSMITH-LENS ──
  fleetReportKind: FleetReportKind,
  fleetReportNarrative: FleetReportNarrative,
  skillsmithOrderKind: SkillsmithOrderKind,
  skillsmithOrderStatus: SkillsmithOrderStatus,
  skillsmithCadence: SkillsmithCadence,
  skillsmithStandingStatus: SkillsmithStandingStatus,
  skillsmithDraftKind: SkillsmithDraftKind,
  skillsmithDraftState: SkillsmithDraftState,
  skillsmithActionKind: SkillsmithActionKind,
  serviceErrorService: ServiceErrorService,
  satelliteIncidentKind: SatelliteIncidentKind,
  satelliteIncidentSeverity: SatelliteIncidentSeverity,
  // ── BILLING-PLANS ──
  maintenanceTier: MaintenanceTier,
  maintenanceSupervision: MaintenanceSupervision,
  collectionMethod: CollectionMethod,
  systemUsageBilling: SystemUsageBilling,
  invoicePaymentMethod: InvoicePaymentMethod,
  subscriptionStatus: SubscriptionStatus,
  invoiceStatus: InvoiceStatus,
  invoiceSource: InvoiceSource,
  discountAppliesTo: DiscountAppliesTo,
  quoteStatus: QuoteStatus,
  billingOperationStatus: BillingOperationStatus,
  chargeKind: ChargeKind,
  buildProjectKind: BuildProjectKind,
  buildProjectStatus: BuildProjectStatus,
  buildMaintenanceShape: BuildMaintenanceShape,
  milestoneStatus: MilestoneStatus,
} as const satisfies { [K in keyof typeof e]: z.ZodEnum };

// Contract-only enums ------------------------------------------------------------------------

/** Feature keys of `requireFeature` (13 §Entitlements): `core` is always on, the rest are add-ons. */
export const FeatureKey = z.enum(["core", ...e.addonKey]);

/**
 * Company relations (13 §Principals and roles; 05 §Model). `member` (MEMBER-ROLE) is the plain
 * employee: Chat, Work, the org chart, its own seat; owner and admin imply it, client_viewer (the
 * external read-only relation) does not.
 */
export const CompanyRelation = z.enum([
  "owner",
  "admin",
  "member",
  "client_viewer",
  "agency_operator",
]);

/** Roles a company manager may give a person through `people` (PATCH `roles`). */
export const PersonRole = z.enum(["owner", "admin", "member", "client_viewer"]);

/** `actor_identity_mode` (02 actor contract); always NULL in the slice. */
export const IdentityMode = z.enum(["as_agent", "on_behalf_of"]);

/** domain-kpi `Band` (health.ts). */
export const Band = z.enum(["great", "regular", "average", "low", "insufficient_data"]);

/** domain-kpi `ScoreState` (scorecard.ts). */
export const ScoreState = z.enum([
  "complete",
  "provisional",
  "missing",
  "context",
  "not_applicable",
  "restricted",
  "unmeasured",
]);

/** Ingest item kinds (13 §Shapes `IngestItem.kind`). */
export const IngestItemKind = z.enum(["activity", "outcome", "cost", "time"]);

/**
 * Per-item ingest receipt status (13 §Ingest contract rule 3). `conflict` (S1-08): the item's
 * `item_key` is already recorded with other content; nothing is written for it and the
 * original stands (`event_id` names it). Never a silent overwrite. `rejected` (S1-08 review #1):
 * the item may not be recorded with this key (`reason`, e.g. a foreign meter namespace).
 */
export const IngestReceiptStatus = z.enum([
  "accepted",
  "duplicate",
  "revised",
  "unmapped",
  "conflict",
  "rejected",
]);

/** Cost kinds an ingest `cost` item may carry (13 §Shapes `IngestItem.cost.cost_kind`). */
export const IngestCostKind = z.enum([
  "de_usage",
  "agent_tokens",
  "agent_compute",
  "agent_tool",
  // lane D: the voice worker's STT minutes and TTS characters (shared contracts §4.2)
  "agent_media",
]);

/** Time-entry grains the ingest/import path accepts: aggregates only, never intervals. */
export const AggregateGrain = z.enum(["day", "week"]);

/**
 * Gate tracker states (13 §Gate tracker). `incomplete`: the population behind the gate was not
 * read in full, so no definitive state is claimed (S1-17 review #5).
 */
export const GateState = z.enum(["on_track", "at_risk", "missed", "met", "incomplete"]);

/** Where a gate's target comes from (S1-17 review #9; domain-kpi `GateTargetSource`). */
export const GateTargetSource = z.enum(["rule", "verdict_fallback", "derived", "none"]);

/** `metric_def.event_query.measure` (13 §metric_def). */
export const EventMeasure = z.enum([
  "count",
  "sum:quantity",
  "sum:value",
  "sum:amount_minor",
  "sum:duration_seconds",
]);

/** CSV import resources (13 §Imports). */
export const ImportResource = z.enum([
  "time-entries",
  "external-identities",
  "observations",
  "costs",
  // M2-08: tasks onto one board (`POST …/boards/{id}/tasks/import`).
  "tasks",
]);

/** Per-row import result. */
export const ImportRowStatus = z.enum(["ok", "duplicate", "revised", "unmapped", "error"]);

/** `friday_report.recipients[].kind`. */
export const RecipientKind = z.enum(["member", "extra"]);

/** `friday_report.context_sources[].kind` (agent-os.md §12.10). */
export const ContextSourceKind = z.enum([
  "publication",
  "ledger",
  "time_entries",
  "observations",
  "note",
]);

/** Chat surfaces a DE's runtime may sit on (`digital_employee.runtime_ref.chat.surface`). */
export const ChatSurface = z.enum(["slack", "telegram"]);
